import React, { createContext, useContext, useEffect } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useStateWithGetter } from '@bld/bld-libs/utils';

import FieldBulletinBoardsService from './FieldBulletinBoardsService';
import FieldBulletinBoardsApi from './FieldBulletinBoardsApi';

import SessionStorageService from '../utilities/SessionStorageService';
import { useFieldBulletinBoardUnreadCount } from '../fieldBulletinBoard/FieldBulletinBoardsUnreadCountContext.js';

import {
  REQUEST_PARAMS,
  IMPORTANCE_TYPE,
  UNREAD_ONLY,
  DATE_TIME_FORMAT,
} from '../../utilities/constants';

const FieldBulletinBoardsCtx = createContext();

function FieldBulletinBoardsProvider({ children }) {
  const defaultSearchParam = {
    title: '',
    bulletin_board_publication_status: '',
    publication_start_at_from: '',
    publication_start_at_to: '',
    importance_type: undefined,
    unread_only: UNREAD_ONLY.UNCHECK,
    limit: REQUEST_PARAMS.parentLimit,
    offset: 0,
  };

  const [, setIsLoading, getIsLoading] = useStateWithGetter(false);
  const [, setFieldInfo, getFieldInfo] = useStateWithGetter({});
  const [page, setPage, getPage] = useStateWithGetter(1);
  const [, setSearchParam, getSearchParam] = useStateWithGetter(
    defaultSearchParam
  ); // 画面上表示している検索条件
  const [, setFixedSearchParam, getFixedSearchParam] = useStateWithGetter(
    defaultSearchParam
  ); // 確定された検索条件
  const [, setIsEditable, getIsEditable] = useStateWithGetter(false);
  const [, setIsDisabled, getIsDisabled] = useStateWithGetter(false);
  const [, setIsMainJv, getIsMainJv] = useStateWithGetter(false);
  const [, setTotal, getTotal] = useStateWithGetter(0);
  const [, setBulletinBoardList, getBulletinBoardList] = useStateWithGetter([]);
  // 調整会議・入退場の機能一覧エリアに表示させる現場掲示板未読件数マークを最新化する。
  const { fetchUnreadCount } = useFieldBulletinBoardUnreadCount();

  /**
   * ライフサイクルメソッド
   * componentDidMount 同様に初回レンダリング時のみ実行される
   * 第二引数に空配列を指定すると初回レンダリング時のみ実行する
   */
  useEffect(() => {
    setIsLoading(true);
    // Buildee利用終了日のチェックは後続で行うためflaseを渡す
    FieldBulletinBoardsService.loadFieldInfo(false)
      .then(response => {
        if (_.isEmpty(response) || !response.isAccessible) {
          // rails.jsxのcheckPermissionsの/callback遷移処理との競合を避けるためcompanyIdの有無で条件分岐
          if (SessionStorageService.getCompanyId()) {
            window.location.replace('/error-permission');
          }
          return
        }

        // 作成権限があり、且つBuildee利用終了日を過ぎている場合は作成ボタンを非活性とする
        if (response.isEditable) {
          const today = moment().format(DATE_TIME_FORMAT.slashDateFormat);
          const usageEndDate = moment(
            new Date(response.fieldInfo.usage_end_date).toISOString()
          ).format(DATE_TIME_FORMAT.slashDateFormat);

          setIsDisabled(moment(today).isAfter(usageEndDate));
        }

        setIsEditable(response.isEditable);
        setFieldInfo(response.fieldInfo);
      })
      .catch(error => {
        return window.location.replace('/error-permission');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  /**
   * ページング処理
   * @param {boolean} init 初期化フラグ
   */
  const getFieldBulletinBoardListByPage = page => {
    const fieldId = SessionStorageService.getFieldId();

    setIsLoading(true);

    const pagedParam = {
      ...getFixedSearchParam(),
      offset: (page - 1) * REQUEST_PARAMS.parentLimit,
      limit: REQUEST_PARAMS.parentLimit,
    };

    FieldBulletinBoardsApi.fetchFieldBulletinBoardList(fieldId, pagedParam)
      .then(response => {
        if (response.result) {
          setTotal(response.data.total);
          setIsMainJv(response.data.sponsor_jv_flag);
          setBulletinBoardList(response.data.entities.field_bullentin_boards);
        }
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getFieldBulletinBoardListByPage(page);
  }, [page]);

  /**
   * お知らせ検索
   * @param {boolean} init 初期化フラグ
   */
  const searchFieldBulletinBoardList = (init = false) => {
    const fieldId = SessionStorageService.getFieldId();
    let param = getSearchParam();

    if (init) {
      param = defaultSearchParam;
      setSearchParam(defaultSearchParam);
    }

    // 検索操作が行われた場合検索条件を固定する
    setFixedSearchParam(param);
    setPage(1);

    setIsLoading(true);

    FieldBulletinBoardsApi.fetchFieldBulletinBoardList(fieldId, param)
      .then(response => {
        if (response.result) {
          setTotal(response.data.total);
          setBulletinBoardList(response.data.entities.field_bullentin_boards);
        }
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * タイトルを入力する
   * @param { string } value:
   */
  const inputTitle = value => {
    setSearchParam(prevState => ({
      ...prevState,
      title: value || '',
    }));
  };

  /**
   * 公開状況を選択する
   */
  const selectBulletinBoardPublicationStatus = value => {
    setSearchParam(prevState => ({
      ...prevState,
      bulletin_board_publication_status: value || '',
    }));
  };

  /**
   * 公開日のfrom日付を設定する
   */
  const setPublicationStartAtFrom = value => {
    setSearchParam(prevState => ({
      ...prevState,
      publication_start_at_from: value || '',
    }));
  };

  /**
   * 公開日のfrom日付がto日付より後だった場合from日付をto日付に合わせる
   */
  const adjustmentPublicationDateAtFrom = value => {
    const publicationStartAtTo = getSearchParam().publication_start_at_to;
    if (!_.isEmpty(value) && !_.isEmpty(publicationStartAtTo)) {
      if (
        moment(new Date(value).toISOString()).isAfter(
          new Date(publicationStartAtTo)
        )
      ) {
        setPublicationStartAtFrom(publicationStartAtTo);
      }
    }
  };

  /**
   * 公開日のto日付を設定する
   */
  const setPublicationStartAtTo = value => {
    setSearchParam(prevState => ({
      ...prevState,
      publication_start_at_to: value || '',
    }));
  };

  /**
   * 公開日のfrom日付がto日付より後だった場合from日付をto日付に合わせる
   */
  const adjustmentPublicationDateAtTo = value => {
    const publicationStartAtFrom = getSearchParam().publication_start_at_from;
    if (!_.isEmpty(value) && !_.isEmpty(publicationStartAtFrom)) {
      if (
        moment(new Date(value).toISOString()).isBefore(
          new Date(publicationStartAtFrom)
        )
      ) {
        setPublicationStartAtTo(publicationStartAtFrom);
      }
    }
  };

  /**
   * 重要なお知らせのみ表示を設定する
   */
  const setImportanceType = event => {
    setSearchParam(prevState => ({
      ...prevState,
      importance_type: event.target.checked ? IMPORTANCE_TYPE.CHECK : undefined,
    }));
  };

  /**
   * 未読のみ表示を設定する
   */
    const setUnreadOnly = event => {
      setSearchParam(prevState => ({
        ...prevState,
        unread_only: event.target.checked ? UNREAD_ONLY.CHECK : UNREAD_ONLY.UNCHECK,
      }));
    };

  /**
   * 伝搬したい値
   */
  const contextValue = {
    defaultSearchParam, // 初期検索条件
    getIsLoading, // API実行中か？
    getFieldInfo, // 現場情報を取得するメソッド
    getSearchParam, // 検索用オブジェクトを取得するメソッド
    getFixedSearchParam, // 確定された検索条件を取得するメソッド
    getTotal, // 検索結果の件数を取得するメソッド
    getBulletinBoardList, // お知らせ一覧を取得するメソッド
    getPage, // 現在のページ番号
    setPage, // ページ番号を設定するメソッド
    searchFieldBulletinBoardList, // お知らせ検索メソッド
    setPublicationStartAtFrom, // 公開日のfrom日付を設定するメソッド
    adjustmentPublicationDateAtFrom,
    setPublicationStartAtTo, // 公開日のto日付を設定するメソッド
    adjustmentPublicationDateAtTo,
    inputTitle, // 検索用タイトルに値を格納するメソッド
    setImportanceType, // 検索用重要なお知らせのみ表示を設定するメソッド
    setUnreadOnly, // 検索用未読のみ表示を設定するメソッド
    getIsEditable, // 新規作成可能か？
    getIsDisabled, // 作成ボタンを活性とするか？
    getIsMainJv, // APIから返却されたスポンサーJVかのフラグ(ボタン表示制御で使用)
    selectBulletinBoardPublicationStatus, // 公開状況に値を格納するメソッド
    setSearchParam, // 表示中の検索条件に値を格納するメソッド
    setFixedSearchParam, // 確定された検索条件に値を格納するメソッド
  };

  return (
    <FieldBulletinBoardsCtx.Provider value={contextValue}>
      {children}
    </FieldBulletinBoardsCtx.Provider>
  );
}

/**
 * Contextを取得する
 * @return {context} context
 */
const useFieldBulletinBoards = () => {
  const context = useContext(FieldBulletinBoardsCtx);
  if (_.isEmpty(typeof context)) {
    throw new Error(
      'FieldBulletinBoardsCtx must be used within FieldBulletinBoardsProvider'
    );
  }
  return context;
};

export { FieldBulletinBoardsProvider, useFieldBulletinBoards };



// WEBPACK FOOTER //
// ./src/services/fieldBulletinBoard/FieldBulletinBoardsContext.js